import React, { useState } from 'react';
import { useAuth, api } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import {
  FileText, ScrollText, CheckCircle2, AlertTriangle, XCircle, Loader2,
  ListOrdered, ReceiptText, Accessibility, Calculator,
} from 'lucide-react';
import { formatPHP } from '../../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const peso = (n) => formatPHP(parseFloat(n) || 0);

const QUARTERS = [
  { label: 'Q1', from: '-01-01', to: '-03-31' },
  { label: 'Q2', from: '-04-01', to: '-06-30' },
  { label: 'Q3', from: '-07-01', to: '-09-30' },
  { label: 'Q4', from: '-10-01', to: '-12-31' },
];

function StatusPill({ ok, warn, label }) {
  const cls = ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : warn ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-red-50 text-red-700 border-red-200';
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border ${cls}`}>
      <Icon size={13} />{label}
    </span>
  );
}

function CheckCard({ icon: Icon, title, status, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
            <Icon size={17} className="text-[#1A4D2E]" />
          </div>
          <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>{title}</h3>
        </div>
        {status}
      </div>
      {children}
    </div>
  );
}

export default function BIRAuditWizard({ defaultBranchId }) {
  const { branches } = useAuth();
  const now = new Date();
  const yr = now.getFullYear();
  const [branchId, setBranchId] = useState(defaultBranchId || '');
  const [from, setFrom] = useState(`${yr}-01-01`);
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const pickQuarter = (q) => { setFrom(`${yr}${q.from}`); setTo(`${yr}${q.to}`); };

  const runScan = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period_from: from, period_to: to });
      if (branchId) params.set('branch_id', branchId);
      const res = await api.get(`${BACKEND_URL}/api/audit/bir-scan?${params}`);
      setData(res.data);
      toast.success('BIR scan complete');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'BIR scan failed');
    }
    setLoading(false);
  };

  const slsp = data?.slsp?.total;

  return (
    <div className="space-y-5" data-testid="bir-audit-wizard">
      {/* Intro + controls */}
      <div className="bg-gradient-to-br from-[#1A4D2E] to-[#14532d] rounded-xl p-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <ScrollText size={18} />
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>BIR Audit Wizard</h2>
        </div>
        <p className="text-sm text-white/70 mb-4 max-w-2xl">
          A dedicated tax-compliance scan: SLSP &amp; output VAT, invoice sequence integrity,
          uninvoiced sales, and SC/PWD logbook completeness — fact-based, filing-ready.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-white/60 mb-1 uppercase tracking-wide">Branch</label>
            <Select value={branchId || 'all'} onValueChange={(v) => setBranchId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-44 bg-white/10 border-white/20 text-white h-9" data-testid="bir-branch-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-white/60 mb-1 uppercase tracking-wide">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 bg-white/10 border-white/20 text-white h-9" data-testid="bir-from-date" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-white/60 mb-1 uppercase tracking-wide">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 bg-white/10 border-white/20 text-white h-9" data-testid="bir-to-date" />
          </div>
          <div className="flex items-center gap-1.5">
            {QUARTERS.map((q) => (
              <button key={q.label} onClick={() => pickQuarter(q)} data-testid={`bir-quarter-${q.label}`}
                className="px-2.5 py-1.5 rounded-md text-xs font-semibold bg-white/10 hover:bg-white/20 border border-white/20 transition-colors">
                {q.label} {yr}
              </button>
            ))}
          </div>
          <Button onClick={runScan} disabled={loading} data-testid="bir-run-scan-btn"
            className="bg-white text-[#1A4D2E] hover:bg-white/90 h-9 px-5 font-semibold ml-auto">
            {loading ? <><Loader2 size={15} className="mr-1.5 animate-spin" />Scanning…</> : <><FileText size={15} className="mr-1.5" />Run BIR Scan</>}
          </Button>
        </div>
      </div>

      {!data ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 flex flex-col items-center text-center">
          <ScrollText size={32} className="text-slate-300 mb-3" />
          <p className="font-semibold text-slate-700">Run a scan to check BIR readiness</p>
          <p className="text-sm text-slate-400 mt-1">Pick a quarter or date range, then Run BIR Scan.</p>
        </div>
      ) : (
        <>
          {/* SLSP / Output VAT */}
          <CheckCard icon={Calculator} title="SLSP & Output VAT" testid="bir-slsp-card"
            status={<StatusPill ok warn={false} label={`${data.issued_count} issued`} />}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                ['Vatable Sales', slsp?.vatable], ['VAT-Exempt', slsp?.exempt],
                ['Zero-Rated', slsp?.zero], ['Output VAT', slsp?.output_vat], ['Gross', slsp?.gross],
              ].map(([l, v]) => (
                <div key={l} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{l}</p>
                  <p className="font-mono font-semibold text-slate-800 mt-0.5">{peso(v)}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-slate-500">
              <span>Sales Invoices (SI): <b className="text-slate-700 font-mono">{data.slsp?.si?.count || 0}</b></span>
              <span>Charge Invoices (CI): <b className="text-slate-700 font-mono">{data.slsp?.ci?.count || 0}</b></span>
            </div>
          </CheckCard>

          {/* Sequence integrity */}
          <CheckCard icon={ListOrdered} title="Invoice Sequence Integrity" testid="bir-sequence-card"
            status={<StatusPill ok={data.sequence_gap_total === 0} warn={false}
              label={data.sequence_gap_total === 0 ? 'No gaps' : `${data.sequence_gap_total} missing`} />}>
            {data.sequence_gap_total === 0 ? (
              <p className="text-sm text-slate-500">All issued SI/CI numbers are accounted for in sequence. ✓</p>
            ) : (
              <div className="space-y-2">
                {data.sequence_gaps.map((g) => (
                  <div key={g.series} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-red-700">{g.series}: {g.missing_count} missing ({g.first} → {g.last})</p>
                    <p className="font-mono text-xs text-red-500 mt-1">{g.missing.join(', ')}{g.missing_count > g.missing.length ? ' …' : ''}</p>
                  </div>
                ))}
                <p className="text-xs text-slate-500">Locate or formally cancel these before filing — BIR requires every number accounted for.</p>
              </div>
            )}
          </CheckCard>

          {/* Unissued sales */}
          <CheckCard icon={ReceiptText} title="Uninvoiced Completed Sales" testid="bir-unissued-card"
            status={<StatusPill ok={data.unissued_count === 0} warn={data.unissued_count > 0}
              label={data.unissued_count === 0 ? 'All invoiced' : `${data.unissued_count} pending`} />}>
            {data.unissued_count === 0 ? (
              <p className="text-sm text-slate-500">Every completed sale has an SI/CI. ✓</p>
            ) : (
              <div>
                <p className="text-sm text-slate-600 mb-2">
                  <b className="font-mono text-amber-700">{peso(data.unissued_value)}</b> across{' '}
                  <b>{data.unissued_count}</b> completed sale(s) have no Sales/Charge Invoice yet.
                </p>
                <div className="max-h-44 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
                  {data.unissued_sample.map((u, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="text-slate-600">{u.invoice_number} · {u.customer_name || 'Walk-in'}</span>
                      <span className="font-mono text-slate-700">{peso(u.grand_total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CheckCard>

          {/* SC/PWD logbook */}
          <CheckCard icon={Accessibility} title="SC/PWD Logbook Completeness" testid="bir-scpwd-card"
            status={<StatusPill ok={data.scpwd_incomplete_count === 0} warn={data.scpwd_incomplete_count > 0}
              label={data.scpwd_total === 0 ? 'No SC/PWD sales' : data.scpwd_incomplete_count === 0 ? 'Complete' : `${data.scpwd_incomplete_count} incomplete`} />}>
            {data.scpwd_total === 0 ? (
              <p className="text-sm text-slate-500">No SC/PWD discounted sales in this period.</p>
            ) : data.scpwd_incomplete_count === 0 ? (
              <p className="text-sm text-slate-500">All {data.scpwd_total} SC/PWD sale(s) have Name + OSCA/PWD ID. ✓</p>
            ) : (
              <div className="space-y-1.5">
                {data.scpwd_incomplete.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-amber-50 border border-amber-100 rounded px-3 py-1.5">
                    <span className="text-slate-700">{s.invoice} · {s.date}</span>
                    <span className="text-amber-700 font-semibold">Missing: {s.missing}</span>
                  </div>
                ))}
              </div>
            )}
          </CheckCard>
        </>
      )}
    </div>
  );
}
